/**
 * Safe localStorage utility with error handling
 * Prevents crashes from localStorage access issues (quota exceeded, disabled, etc.)
 */

export const safeLocalStorage = {
  /**
   * Safely get an item from localStorage
   * @param key - The localStorage key
   * @param defaultValue - Value to return if retrieval fails
   * @returns The stored value or defaultValue
   */
  getItem: <T = string>(key: string, defaultValue: T | null = null): T | null => {
    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return defaultValue;
      }
      // Try to parse as JSON if it looks like JSON
      if (item.startsWith('{') || item.startsWith('[')) {
        try {
          return JSON.parse(item) as T;
        } catch {
          // If JSON parsing fails, return as string
          return item as unknown as T;
        }
      }
      return item as unknown as T;
    } catch (error) {
      console.error(`Failed to get item from localStorage (key: ${key}):`, error);
      return defaultValue;
    }
  },

  /**
   * Safely set an item in localStorage
   * @param key - The localStorage key
   * @param value - The value to store (will be JSON stringified if object/array)
   * @returns true if successful, false otherwise
   */
  setItem: (key: string, value: any): boolean => {
    try {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(key, stringValue);
      return true;
    } catch (error) {
      console.error(`Failed to set item in localStorage (key: ${key}):`, error);
      // Check if it's a quota exceeded error
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.warn('localStorage quota exceeded. Consider clearing old data.');
      }
      return false;
    }
  },

  /**
   * Safely remove an item from localStorage
   * @param key - The localStorage key
   * @returns true if successful, false otherwise
   */
  removeItem: (key: string): boolean => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Failed to remove item from localStorage (key: ${key}):`, error);
      return false;
    }
  },

  /**
   * Safely clear all items from localStorage
   * @returns true if successful, false otherwise
   */
  clear: (): boolean => {
    try {
      localStorage.clear();
      return true;
    } catch (error) {
      console.error('Failed to clear localStorage:', error);
      return false;
    }
  },

  /**
   * Check if localStorage is available and working
   * @returns true if localStorage is available, false otherwise
   */
  isAvailable: (): boolean => {
    try {
      const testKey = '__localStorage_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  },
};

